import type { GraphEdge, GraphNode } from '../../../types'

export type RootRelationDirection = 'owns-root' | 'owned-by-root'

export interface RootRelationLine {
  kind: 'legal' | 'beneficial'
  direction: RootRelationDirection
  capitalMin: number | null
  capitalMax: number | null
  /** Number of distinct paths that produced this stake (legal only). */
  pathCount: number
}

export interface RootRelationSummary {
  isRoot: boolean
  lines: RootRelationLine[]
}

function stakeKey(line: Pick<RootRelationLine, 'kind' | 'direction' | 'capitalMin' | 'capitalMax'>): string {
  return `${line.kind}|${line.direction}|${line.capitalMin ?? 'n'}|${line.capitalMax ?? 'n'}`
}

function multiplyPathPercents(edges: GraphEdge[]): { min: number | null; max: number | null } {
  let minFactor = 1
  let maxFactor = 1
  for (const e of edges) {
    if (e.capitalPercent == null || Number.isNaN(e.capitalPercent)) {
      return { min: null, max: null }
    }
    minFactor *= e.capitalPercent / 100
    const hi = e.capitalPercentMax ?? e.capitalPercent
    if (hi == null || Number.isNaN(hi)) {
      return { min: null, max: null }
    }
    maxFactor *= hi / 100
  }
  return {
    min: minFactor * 100,
    max: maxFactor * 100,
  }
}

function findAllLegalPaths(start: string, end: string, edges: GraphEdge[]): GraphEdge[][] {
  const paths: GraphEdge[][] = []
  const legal = edges.filter(e => e.kind === 'legal')

  function dfs(current: string, path: GraphEdge[], visited: Set<string>) {
    if (current === end) {
      paths.push([...path])
      return
    }
    for (const e of legal) {
      if (e.from !== current || visited.has(e.to)) continue
      visited.add(e.to)
      path.push(e)
      dfs(e.to, path, visited)
      path.pop()
      visited.delete(e.to)
    }
  }

  dfs(start, [], new Set([start]))
  return paths
}

function mergeLegalPathStakes(
  paths: GraphEdge[][],
  direction: RootRelationDirection,
): RootRelationLine[] {
  const counts = new Map<string, RootRelationLine & { pathCount: number }>()

  for (const path of paths) {
    const { min, max } = multiplyPathPercents(path)
    if (min == null) continue
    const line: RootRelationLine = {
      kind: 'legal',
      direction,
      capitalMin: min,
      capitalMax: max != null && Math.abs(max - min) > 0.005 ? max : null,
      pathCount: 1,
    }
    const key = stakeKey(line)
    const existing = counts.get(key)
    if (existing) {
      existing.pathCount += 1
    } else {
      counts.set(key, { ...line })
    }
  }

  return [...counts.values()].sort((a, b) => (a.capitalMin ?? 0) - (b.capitalMin ?? 0))
}

/**
 * Effective ownership stakes between a graph node and the root — products
 * along all legal paths (no step-by-step chain; callers format for display).
 */
export function computeRootRelation(
  nodeId: string,
  rootId: string,
  _nodes: GraphNode[],
  edges: GraphEdge[],
): RootRelationSummary {
  if (nodeId === rootId) {
    return { isRoot: true, lines: [] }
  }

  const lines: RootRelationLine[] = []

  for (const e of edges) {
    if (e.kind !== 'beneficial' || e.from !== nodeId || e.to !== rootId) continue
    lines.push({
      kind: 'beneficial',
      direction: 'owns-root',
      capitalMin: e.capitalPercent ?? null,
      capitalMax: e.capitalPercentMax ?? null,
      pathCount: 1,
    })
  }

  const upstreamPaths = findAllLegalPaths(nodeId, rootId, edges)
  lines.push(...mergeLegalPathStakes(upstreamPaths, 'owns-root'))

  const downstreamPaths = findAllLegalPaths(rootId, nodeId, edges)
  lines.push(...mergeLegalPathStakes(downstreamPaths, 'owned-by-root'))

  return { isRoot: false, lines }
}
